import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Drawer } from '@material-tailwind/react';
import Buttons from '../../../common/Buttons';
import TextWinds from '../../../common/TextWinds';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../store/hook';
import {
  getBoostupInfo,
  selectBoostupInfo,
} from '../../../../store/features/boostupSlice';
import { useCallback, useEffect, useState } from 'react';
import FullWidthPage from '../../../layout/FullWidthPage';
import { BoostUpTypes, ChosenVariantDto } from '../../../../constants/types';
import { Options } from '../../../options/options';
import { getOrderItem } from '../../../../api/utils/common-purchase';
import { resetPurchaseInfo } from '../../../../store/features/purchaseSlice';
import { excuteAxiosPost } from '../../../../api/axios/call/testAxiosCall';
import { addCart } from '../../../../store/features/cartSlice';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import useUserHook from '../../../../controllers/useUserHook';
import BoostupWmwBookMain from './book/BoostupWmwBookMain';
import BoostupJSBookMain from './book/BoostupJSBookMain';
import BoostupHFBookMain from './book/BoostupHFBookMain';
import BoostupHJBookMain from './book/BoostupHJBookMain';
import renderDetailContents from '../../../../shared/renderDetailContents';
import { getInterestFreeInstallmentMessage } from '../../../../utils/interest-free-installment';
import {
  isProductPurchasable,
  PRODUCT_INACTIVE_LABEL,
  PRODUCT_INACTIVE_MESSAGE,
} from '../../../../utils/product-active';

const BoostUpDetail = () => {
  const { checkWithCallback } = useUserHook();
  const { boostupId } = useParams();
  const path = '/v1/product/' + boostupId;
  const dispatch = useAppDispatch();
  const boostupInfo: BoostUpTypes = useAppSelector(selectBoostupInfo);

  useEffect(() => {
    dispatch(getBoostupInfo({ url: path }));
  }, [path]);

  const isLoading = (): boolean => {
    if (!boostupInfo) {
      return true;
    } else {
      return false;
    }
  };

  /** 판매 중지(is_active === false) 상품이면 구매 불가 */
  const isPurchasable = isProductPurchasable(boostupInfo);

  const [openBottom, setOpenBottom] = useState(false);
  const openDrawerBottom = () => {
    setOpenBottom(true);
  };
  const closeDrawerBottom = () => {
    setOpenBottom(false);
  };

  const navigate = useNavigate();

  const onPurchaseHandle = async (
    childId: string,
    selectOptionData: ChosenVariantDto[],
  ) => {
    if (!isPurchasable) {
      dispatch(openAlert({ message: PRODUCT_INACTIVE_MESSAGE }));
      return;
    }
    const action = async () => {
      const path = '/v1/order/template/from-prod';
      const params = {
        order_item: getOrderItem({
          product_id: childId,
          variants: selectOptionData,
          options: [],
          enrolls: [],
        }),
      };
      await Promise.all([excuteAxiosPost(path, params)]).then((res) => {
        if (res) {
          dispatch(resetPurchaseInfo());
          navigate(
            { pathname: '/purchase' },
            { state: { purchaseId: res[0].data.id } },
          );
        }
      });
    };
    return await checkWithCallback(action);
  };
  const onCartHandle = useCallback(
    async (_childId: string, selectItem: ChosenVariantDto[]) => {
      if (!isPurchasable) {
        dispatch(openAlert({ message: PRODUCT_INACTIVE_MESSAGE }));
        return;
      }
      const action = async () => {
        if (selectItem.length > 0) {
          try {
            const res = await dispatch(
              addCart({
                url: '/v1/cart',
                data: {
                  order_item: getOrderItem({
                    product_id: boostupInfo.id,
                    variants: selectItem,
                    options: [],
                    enrolls: [],
                  }),
                },
              }),
            ).unwrap();

            if (res) {
              dispatch(
                openConfirm({
                  message: '장바구니에 추가되었습니다.',
                  onConfirm: () => {
                    navigate('/cart');
                  },
                  confirmText: '장바구니 보러 가기',
                  cancelText: '상품 계속 담기',
                }),
              );
            }
          } catch (e) {
            console.log('e', e);
            dispatch(
              openAlert({
                message:
                  '일시적인 장애로 상품을 장바구니에 담을 수 없습니다. 나중에 다시 시도해주세요.',
              }),
            );
          }
        }
      };
      return await checkWithCallback(action);
    },
    [boostupInfo, isPurchasable],
  );

  const infoBtnClick = async () => {
    const message = await getInterestFreeInstallmentMessage();
    dispatch(openAlert({ message }));
    // dispatch(openAlert({ message: INTEREST_FREE_INSTALLMENT_DEFAULT }));
  };

  return isLoading() ? null : (
    <FullWidthPage>
      {boostupInfo && (
        <div className="flex flex-col gap-y-6 pt-10">
          <TextWinds type="title_h2" xlType="title_h1" className="px-4">
            {boostupInfo.name}
          </TextWinds>
          <div className="flex flex-col gap-[32px] lg:flex-row lg:pt-[24px]">
            <div className="flex w-full shrink-0 lg:w-[275px]">
              <div className="sticky top-20 h-fit w-full bg-gray0 p-[10px]">
                <div className="relative w-full">
                  <TextWinds type="title_h6" color="gray7">
                    설명
                  </TextWinds>
                  <TextWinds type="content_rel" className="pt-[4px]">
                    {boostupInfo.description}
                  </TextWinds>
                </div>
                <div className="mt-[16px] bg-white p-[10px]">
                  <div
                    className="flex cursor-pointer items-center gap-[10px] p-[10px]"
                    onClick={infoBtnClick}
                  >
                    <InformationCircleIcon className="relative h-[24px] w-[24px]" />
                    <TextWinds type="content_rel">무이자 할부 안내</TextWinds>
                  </div>
                  <div className="flex w-full flex-col items-center justify-center gap-[13px] pt-[13px]">
                    {/* <Buttons type="filled" disabled color={"purple5"}>
                      이용중 ~22.09.10 까지
                    </Buttons> */}
                    <Buttons
                      type="filled"
                      onClick={openDrawerBottom}
                      color={'purple5'}
                      disabled={!isPurchasable}
                    >
                      {isPurchasable ? '구매하기' : PRODUCT_INACTIVE_LABEL}
                    </Buttons>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex basis-full flex-col overflow-hidden">
              {(() => {
                switch (boostupInfo.id) {
                  // WMW 교재 구매 페이지
                  case 'DT2tAtPS0TTZ91I8':
                    return <BoostupWmwBookMain />;

                  // JS1 교재 구매 페이지
                  case 'k6fN19d9lAIhnqS-':
                    return <BoostupJSBookMain />;

                  // JS2 교재 구매 페이지
                  case '8NF28UMHVb8pKBEk':
                    return <BoostupJSBookMain />;

                  // HF 교재 구매 페이지
                  case 'gFmNsScTxWA3k0XL':
                    return <BoostupHFBookMain />;

                  // HJ 교재 구매 페이지
                  case '0-htmPre-gUKUXLv':
                    return <BoostupHJBookMain />;

                  default:
                    return (
                      <>
                        {boostupInfo.contents &&
                          renderDetailContents({
                            contents: [...boostupInfo.contents],
                            productName: boostupInfo.name,
                            imageAltPrefix: '상품',
                          })}
                      </>
                    );
                }
              })()}
            </div>

            <div
              className={`fixed bottom-0 left-0 w-full p-4 ${
                openBottom ? 'block' : 'hidden'
              }`}
            >
              <Drawer
                placement="bottom"
                open={openBottom}
                onClose={closeDrawerBottom}
                overlay={false}
                size={500}
                // className="items-center w-1/2 px-5 py-4 left-1/4 right-1/4"
                className="relative right-1/4 m-auto w-full items-center rounded-2xl px-5 py-4 drop-shadow-xl lg:w-1/2"
              >
                <Options
                  gubun="boostup"
                  shopInfo={boostupInfo}
                  chosenVariants={[...boostupInfo.option_variants].sort(
                    (a, b) => (a.name > b.name ? 1 : -1),
                  )}
                  closeDrawerBottom={closeDrawerBottom}
                  onCartHandle={onCartHandle}
                  onPurchaseHandle={onPurchaseHandle}
                  isPointProduct={false}
                />
              </Drawer>
            </div>
          </div>
        </div>
      )}
    </FullWidthPage>
  );
};

export default BoostUpDetail;
